import {useState} from 'react'
import {AvailabilityBoardPage} from './AvailabilityBoardPage'
import {AvailabilityPage} from './AvailabilityPage'

type Layout = 'board' | 'grid'

const OPTIONS: {value: Layout; label: string; icon: string; title: string}[] = [
  {
    value: 'board',
    label: 'Everyone',
    icon: 'bi-grid-3x3',
    title: 'The whole roster, one day per cell',
  },
  {
    value: 'grid',
    label: 'One person',
    icon: 'bi-clock-history',
    title: 'One employee, painted half hour by half hour',
  },
]

/**
 * The availability route's two layouts and the switch between them.
 *
 * Both edit exactly the same data through the same endpoints; what differs is
 * the unit of work. The board answers "who can work on Thursday" across the
 * roster and takes a day at a time; the paint grid answers "when can this
 * person work" and takes half hours. Neither is a superset of the other, so
 * the switch stays rather than one replacing the other.
 *
 * The switch is rendered by whichever page is showing (as its `layoutToggle`),
 * so it sits in that page's own card header instead of a second card above it.
 */
export const AvailabilityView = () => {
  const [layout, setLayout] = useState<Layout>('board')

  const toggle = (
    <div className='btn-group btn-group-sm' role='group' aria-label='Availability layout'>
      {OPTIONS.map((option) => {
        const active = layout === option.value
        return (
          <button
            key={option.value}
            type='button'
            aria-pressed={active}
            title={option.title}
            data-testid={`availability-layout-${option.value}`}
            className={`btn btn-sm ${active ? 'btn-primary' : 'btn-light'}`}
            onClick={() => setLayout(option.value)}
          >
            <i className={`bi ${option.icon} fs-8 me-2`} aria-hidden='true' />
            {option.label}
          </button>
        )
      })}
    </div>
  )

  return layout === 'board' ? (
    <AvailabilityBoardPage layoutToggle={toggle} />
  ) : (
    <AvailabilityPage layoutToggle={toggle} />
  )
}
